import React, { Component } from "react";
import PropTypes from "prop-types";
import { Header, Segment, Button, Modal } from "semantic-ui-react";
import { Link } from "react-router-dom";
import Overridable from "react-overridable";
import _get from "lodash/get";
import { RedirectToLoginButton } from "@authentication/components/RedirectToLoginButton";
import { AuthenticationGuard } from "@authentication/components/AuthenticationGuard";
import { ILSImagePlaceholder } from "@components/ILSPlaceholder";
import { InfoMessage } from "@components/InfoMessage";
import { DocumentEItems } from "./DocumentEItems";
import { LoanRequestForm } from "../LoanRequestForm";
import { LoanInformationBullets } from "./LoanInformationBullets";
import BookCardModalWrapper from "@pages/frontsite/Documents/DocumentDetails/LoanRequestForm/SlipForm/BookCard/components/BookCardModalWrapper";
import { FrontSiteRoutes } from "@routes/urls";
import { loanApi } from "@api/loans";
import { invenioConfig } from "@config";
import { sessionManager } from "@authentication/services/SessionManager";
import { isLibrarianOrAdmin } from "UserRoleUtils";

const LOAN_STATUS = {
  REQUESTABLE: "requestable",
  PENDING: "pending",
  ON_LOAN: "on_loan",
};

const ERROR_MESSAGES = {
  PATRON_NOT_AVAILABLE: "Patron information not available",
  PATRON_NAME_NOT_FOUND: "Patron name not found",
  PATRON_NAME_EMPTY: "Patron name is empty",
  DOCUMENT_MISSING: "Document information is missing",
  CIRCULATION_NOT_CONFIGURED: "Circulation system is not configured",
  LOAN_STATES_NOT_CONFIGURED: "Loan states are not configured",
  LOAN_FETCH_ERROR: "Unable to load loan information. Please try again later.",
  LOAN_INFO_NOT_AVAILABLE: "Loan information not available",
  LOAN_HISTORY_NOT_AVAILABLE: "Loan history data not available",
  CIRCULATION_INFO_NOT_AVAILABLE: "Circulation information not available",
  DOCUMENT_NOT_AVAILABLE: "Document information is not available.",
};

const DEFAULT_VALUES = {
  TITLE: "Title not available",
  AUTHOR: "Author not available",
  FIRST_NAME: "",
  LAST_NAME: "",
  PATRON_NAME: "",
  CHECK_OUT_DATE: "",
  DUE_DATE: "",
  START_DATE: "Start date not available",
  END_DATE: "End date not available",
  LAST_LOAN: "Last loan date not available",
  NO_ACTIVE_LOAN: "No active loan data available",
};

class DocumentCirculation extends Component {
  constructor(props) {
    super(props);
    this.bookCardRef = React.createRef();
  }

  state = {
    showBookCardModal: false,
    activeLoanData: null,
    loadingLoanData: false,
    errorMessage: null,
  };

  extractPatronName = (loan) => {
    if (!loan?.metadata?.patron) {
      return { success: false, error: ERROR_MESSAGES.PATRON_NOT_AVAILABLE };
    }

    const rawName = loan.metadata.patron.name;

    if (!rawName || typeof rawName !== "string") {
      return { success: false, error: ERROR_MESSAGES.PATRON_NAME_NOT_FOUND };
    }

    const fullName = rawName.trim();
    if (fullName.length === 0) {
      return { success: false, error: ERROR_MESSAGES.PATRON_NAME_EMPTY };
    }

    return { success: true, data: fullName };
  };

  parsePatronName = (fullName) => {
    const nameParts = fullName.split(" ");
    return {
      firstName: nameParts[0] || DEFAULT_VALUES.FIRST_NAME,
      lastName: nameParts.slice(1).join(" ") || DEFAULT_VALUES.LAST_NAME,
    };
  };

  getCirculationConfig = () => {
    const circulation = invenioConfig?.CIRCULATION;
    if (!circulation) {
      return {
        success: false,
        error: ERROR_MESSAGES.CIRCULATION_NOT_CONFIGURED,
      };
    }

    const { loanCompletedStates = [], loanActiveStates = ["ITEM_ON_LOAN"] } =
      circulation;

    const allStates = [...loanCompletedStates, ...loanActiveStates];

    if (allStates.length === 0) {
      return {
        success: false,
        error: ERROR_MESSAGES.LOAN_STATES_NOT_CONFIGURED,
      };
    }

    return { success: true, data: allStates };
  };

  setError = (errorMessage) => {
    this.setState({
      activeLoanData: null,
      loadingLoanData: false,
      errorMessage,
    });
  };

  setLoadingState = (loading, callback) => {
    this.setState({ loadingLoanData: loading }, callback);
  };

  fetchActiveLoanData = async () => {
    const { documentDetails } = this.props;

    if (!documentDetails?.pid) {
      return this.setError(ERROR_MESSAGES.DOCUMENT_MISSING);
    }

    const configResult = this.getCirculationConfig();
    if (!configResult.success) {
      return this.setError(configResult.error);
    }

    this.setLoadingState(true);
    this.setState({ errorMessage: null });

    try {
      const query = loanApi
        .query()
        .withDocPid(documentDetails.pid)
        .withState(configResult.data)
        .withSize(1000)
        .sortByNewest()
        .qs();

      const response = await loanApi.list(query);
      const loans =
        response?.data?.hits?.map((hit) => hit._source || hit) || [];

      this.setState({
        activeLoanData: loans,
        errorMessage: null,
      });
    } catch (error) {
      this.setError(ERROR_MESSAGES.LOAN_FETCH_ERROR);
    } finally {
      this.setLoadingState(false);
    }
  };

  handleViewBookCard = () => {
    this.setState({ showBookCardModal: true });
    this.fetchActiveLoanData();
  };

  handleCloseBookCardModal = () => {
    this.setState({
      showBookCardModal: false,
      activeLoanData: null,
      loadingLoanData: false,
      errorMessage: null,
    });
  };

  getBaseDocumentData = () => {
    const { documentDetails } = this.props;
    return {
      title: _get(documentDetails, "metadata.title", DEFAULT_VALUES.TITLE),
      author: _get(
        documentDetails,
        "metadata.authors[0].full_name",
        DEFAULT_VALUES.AUTHOR
      ),
      document: documentDetails,
    };
  };

  getBookCardData = () => {
    const { documentDetails, loansInfo } = this.props;
    const { activeLoanData } = this.state;

    if (!documentDetails?.metadata) {
      return {
        ...this.getBaseDocumentData(),
        firstName: DEFAULT_VALUES.FIRST_NAME,
        lastName: DEFAULT_VALUES.LAST_NAME,
        patronName: DEFAULT_VALUES.PATRON_NAME,
        checkOutDate: DEFAULT_VALUES.CHECK_OUT_DATE,
        dueDate: DEFAULT_VALUES.DUE_DATE,
        hasActiveLoan: false,
        hasRealData: false,
        error: ERROR_MESSAGES.DOCUMENT_MISSING,
      };
    }

    const baseData = this.getBaseDocumentData();

    // Handle active loan data
    if (Array.isArray(activeLoanData) && activeLoanData.length > 0) {
      const firstLoan = activeLoanData[0];
      const patronResult = this.extractPatronName(firstLoan);

      if (!patronResult.success) {
        return {
          ...baseData,
          firstName: DEFAULT_VALUES.FIRST_NAME,
          lastName: DEFAULT_VALUES.LAST_NAME,
          patronName: DEFAULT_VALUES.PATRON_NAME,
          checkOutDate: DEFAULT_VALUES.CHECK_OUT_DATE,
          dueDate: DEFAULT_VALUES.DUE_DATE,
          hasActiveLoan: false,
          hasRealData: false,
          error: patronResult.error,
        };
      }

      const { firstName, lastName } = this.parsePatronName(patronResult.data);

      return {
        ...baseData,
        firstName,
        lastName,
        patronName: patronResult.data,
        checkOutDate: _get(
          firstLoan,
          "metadata.start_date",
          DEFAULT_VALUES.START_DATE
        ),
        dueDate: _get(firstLoan, "metadata.end_date", DEFAULT_VALUES.END_DATE),
        hasActiveLoan: true,
        hasRealData: true,
      };
    }

    if (!loansInfo) {
      return {
        ...baseData,
        firstName: DEFAULT_VALUES.FIRST_NAME,
        lastName: DEFAULT_VALUES.LAST_NAME,
        patronName: DEFAULT_VALUES.PATRON_NAME,
        checkOutDate: DEFAULT_VALUES.CHECK_OUT_DATE,
        dueDate: DEFAULT_VALUES.DUE_DATE,
        hasActiveLoan: false,
        hasRealData: false,
        error: ERROR_MESSAGES.LOAN_INFO_NOT_AVAILABLE,
      };
    }

    return {
      ...baseData,
      firstName: DEFAULT_VALUES.FIRST_NAME,
      lastName: DEFAULT_VALUES.LAST_NAME,
      patronName: DEFAULT_VALUES.NO_ACTIVE_LOAN,
      checkOutDate: _get(loansInfo, "last_loan", DEFAULT_VALUES.LAST_LOAN),
      dueDate: DEFAULT_VALUES.DUE_DATE,
      hasActiveLoan: _get(loansInfo, "has_active_loan", false),
      hasRealData: false,
    };
  };

  getAllLoanHistory = () => {
    const { activeLoanData, errorMessage } = this.state;

    if (errorMessage) {
      return { success: false, error: errorMessage };
    }

    if (!Array.isArray(activeLoanData)) {
      return {
        success: false,
        error: ERROR_MESSAGES.LOAN_HISTORY_NOT_AVAILABLE,
      };
    }

    return { success: true, data: activeLoanData };
  };

  renderInfoMessage = (message) => <InfoMessage message={message} />;

  renderLoginToLoan = () => {
    const circulation = _get(
      this.props,
      "documentDetails.metadata.circulation"
    );
    if (!circulation) {
      return this.renderInfoMessage(
        ERROR_MESSAGES.CIRCULATION_INFO_NOT_AVAILABLE
      );
    }
    return (
      <>
        <LoanInformationBullets circulation={circulation} />
        <RedirectToLoginButton content="Sign in to loan" fluid positive />
      </>
    );
  };

  renderLoanRequestForm = () => {
    const { documentDetails, loansInfo } = this.props;

    if (!loansInfo) {
      return this.renderInfoMessage(ERROR_MESSAGES.LOAN_INFO_NOT_AVAILABLE);
    }

    return (
      <LoanRequestForm
        document={documentDetails}
        lastLoan={_get(loansInfo, "last_loan")}
        onViewBookCard={this.handleViewBookCard}
      />
    );
  };

  renderMyLoansButton = () => (
    <Link
      to={{
        pathname: FrontSiteRoutes.patronProfile,
        state: { focusTab: "activity" },
      }}
    >
      <Button fluid>View Your Loans</Button>
    </Link>
  );

  renderViewBookCardButton = () => {
    const user = sessionManager?.user;
    const hasLibrarianRole =
      user?.roles?.includes("librarian") || user?.roles?.includes("LIBRARIAN");
    const hasAdminRole =
      user?.roles?.includes("admin") ||
      user?.roles?.includes("ADMIN") ||
      user?.roles?.includes("administrator");

    const isAuthorized =
      hasLibrarianRole || hasAdminRole || isLibrarianOrAdmin(user);

    if (!isAuthorized) {
      return null;
    }

    return (
      <Button
        fluid
        style={{
          backgroundColor: "orange",
          color: "white",
          marginBottom: "10px",
        }}
        onClick={this.handleViewBookCard}
      >
        View Book Card
      </Button>
    );
  };

  renderRequestable = () => (
    <>
      <AuthenticationGuard
        authorizedComponent={this.renderLoanRequestForm}
        loginComponent={this.renderLoginToLoan}
      />
      {this.renderViewBookCardButton()}
    </>
  );

  renderPendingRequest = () => (
    <>
      {this.renderInfoMessage("You have requested a loan for this literature.")}
      {this.renderViewBookCardButton()}
      {this.renderMyLoansButton()}
    </>
  );

  renderOnLoan = () => {
    const message = (
      <>
        You have an ongoing loan for this literature. You may request an
        extension on <Link to={FrontSiteRoutes.patronProfile}>Your loans</Link>{" "}
        page.
      </>
    );
    return (
      <>
        {this.renderInfoMessage(message)}
        {this.renderViewBookCardButton()}
        {this.renderMyLoansButton()}
      </>
    );
  };

  renderBookCardModal = () => {
    const { showBookCardModal, loadingLoanData } = this.state;
    const { documentDetails } = this.props;

    const bookCardData = this.getBookCardData();
    const loanHistoryResult = this.getAllLoanHistory();

    return (
      <Modal
        open={showBookCardModal}
        onClose={this.handleCloseBookCardModal}
        size="small"
        closeIcon
      >
        <Modal.Content style={{ padding: "20px" }}>
          {loadingLoanData ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <div>Loading complete loan history from all patrons...</div>
            </div>
          ) : !loanHistoryResult.success ? (
            <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
              {this.renderInfoMessage(loanHistoryResult.error)}
            </div>
          ) : (
            <BookCardModalWrapper
              formData={bookCardData}
              document={documentDetails}
              loanHistory={loanHistoryResult.data}
              showTitle
              showSubtitle
            />
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.handleCloseBookCardModal}>Close</Button>
        </Modal.Actions>
      </Modal>
    );
  };

  getLoanStatus = (loansInfo) => {
    const safeLoansInfo = loansInfo || {};
    const {
      has_active_loan: userHasActiveLoan = false,
      is_requested: userHasPendingRequest = false,
    } = safeLoansInfo;

    if (userHasPendingRequest) return LOAN_STATUS.PENDING;
    if (userHasActiveLoan) return LOAN_STATUS.ON_LOAN;
    return LOAN_STATUS.REQUESTABLE;
  };

  renderLoanStatusComponent = (status) => {
    switch (status) {
      case LOAN_STATUS.PENDING:
        return this.renderPendingRequest();
      case LOAN_STATUS.ON_LOAN:
        return this.renderOnLoan();
      default:
        return this.renderRequestable();
    }
  };

  render() {
    const {
      documentDetails,
      isLoading = false,
      loansInfo = null,
      loanRequestIsLoading = false,
      showTab,
    } = this.props;

    if (!documentDetails?.metadata) {
      return (
        <Segment className="highlighted fs-segment-transparent">
          <div style={{ textAlign: "center", padding: "20px" }}>
            {this.renderInfoMessage(ERROR_MESSAGES.DOCUMENT_NOT_AVAILABLE)}
          </div>
        </Segment>
      );
    }

    const loanStatus = this.getLoanStatus(loansInfo);

    return (
      <>
        <Segment
          loading={loanRequestIsLoading}
          className="highlighted fs-segment-transparent"
        >
          <ILSImagePlaceholder style={{ height: 400 }} isLoading={isLoading}>
            <DocumentEItems
              eitems={_get(documentDetails, "metadata.eitems", {})}
              showTab={showTab}
              documentDetails={documentDetails}
            />
            <Header as="h3" className="mt-10" content="Request Loan" />
            {this.renderLoanStatusComponent(loanStatus)}
            <Overridable
              id="DocumentCirculation.Extras"
              documentDetails={documentDetails}
            />
          </ILSImagePlaceholder>
        </Segment>

        {this.renderBookCardModal()}
      </>
    );
  }
}

DocumentCirculation.propTypes = {
  documentDetails: PropTypes.object.isRequired,
  loansInfo: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  showTab: PropTypes.func.isRequired,
  loanRequestIsLoading: PropTypes.bool.isRequired,
};

export default Overridable.component(
  "DocumentCirculation",
  DocumentCirculation
);
